import type { Moment } from 'moment';

export interface ApiResponse<T> {
  readonly code: number;
  readonly message: string;
  readonly data: T;
}

export type RequestResult<T> = T | Error | undefined;

export function isApiResponse<T>(
  value: RequestResult<ApiResponse<T>>,
): value is ApiResponse<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof value.code === 'number' &&
    'message' in value &&
    typeof value.message === 'string' &&
    'data' in value
  );
}

export function responseCode(value: unknown): number | undefined {
  return typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof value.code === 'number'
    ? value.code
    : undefined;
}

export function responseMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  return typeof value === 'object' && value !== null && 'message' in value
    ? String(value.message)
    : '';
}

export function responseData<T>(value: unknown): T | undefined {
  return typeof value === 'object' && value !== null && 'data' in value
    ? (value.data as T)
    : undefined;
}

export type QueryPrimitive = string | number | boolean | null | undefined;
export type QueryParams = Readonly<Record<string, QueryPrimitive>>;
export type MutablePayload = Record<string, unknown>;

export interface SchoolTypeTrack {
  readonly name: string;
  readonly gradeNums: readonly number[];
}

export interface School {
  readonly id?: string | number;
  readonly name?: string;
  readonly fullName?: string;
  readonly logo?: string;
  readonly slug?: string;
  readonly url?: string;
  readonly city?: string;
  readonly schoolTypeTracks?: readonly SchoolTypeTrack[];
}

export interface AdministrationNode {
  readonly id: string | number;
  readonly name: string;
  readonly level: 'province' | 'city' | 'district' | 'street' | string;
  readonly children?: readonly AdministrationNode[];
}

export interface SsoSiteConfig {
  readonly webRedirectURL?: string | null;
  readonly adminRedirectURL?: string | null;
}

export interface MainSession {
  readonly deploymentStyle?: string;
  readonly serverTime: number;
  readonly sso?: Readonly<Record<string, SsoSiteConfig>>;
}

export interface SoftwareFileInfo {
  readonly version: string;
  readonly fileName?: string;
  readonly mode?: string;
  readonly changelog?: string;
  readonly created_at?: string;
  readonly hash?: string | null;
  readonly file_size?: number | null;
  readonly ext?: string | null;
}

export type SoftwareFileMap = Readonly<Record<string, SoftwareFileInfo>>;

export interface DownloadRecord {
  readonly updated_at: string;
}

export interface ActiveUserSchoolRow {
  readonly schoolName: string;
  readonly count: number;
}

export interface ActiveUserDateRow {
  readonly day: string;
  readonly count: number;
}

export interface CourseSchoolRow {
  readonly schoolName: string;
  readonly courseCount: number;
}

export interface CourseDateRow {
  readonly day: string;
  readonly count: number;
}

export interface StatisticsSearchRequest {
  readonly start?: string | Moment;
  readonly end?: string | Moment;
  readonly minPeople?: number;
  readonly maxPeople?: number;
  readonly nameSearch?: string;
}

export interface RegistrationSchoolInfo extends MutablePayload {
  fullName?: string;
  logo?: string;
  slug?: string;
  administrationID?: string | number;
  computers?: string | number;
  users?: string | number;
  schoolTypeTracks?: SchoolTypeTrack[];
  registerName?: string;
  registerEmail?: string;
  registerCellphone?: string;
  registerQQ?: string;
  registerPassword?: string;
  registerCertifcate?: string;
  captcha?: string;
  passwordValidate?: string;
}

export interface VersionFormValue {
  readonly version?: string | undefined;
  readonly changelog?: string | undefined;
  readonly mode?: string | undefined;
}

export interface UploadPathRecord {
  readonly hash: string;
  readonly path: string;
  readonly fileName: string;
  readonly size: number;
}
